import {styled} from "@reside/ui";
import {useParams} from "react-router-dom";
import {useEventTriggersQuery} from "./hooks/useEventTriggersQuery";
import {Spinner} from "../../atoms/spinner";
import {ButtonLink} from "../../atoms/button-link";
import {EventTriggerViewerForm} from "./EventTriggerViewerForm";
import {getWorkflowLabel} from "../../atoms/start-workflow/workflowMapping";
import {HubAndSpokePage} from "./atoms/HubAndSpokePage";
import {
  FormCardWrapper,
  FormSection,
  FormSectionHint,
  FormSectionTitle,
} from "./styles";
import {getUploadOnLabel} from "./consts";

const EVENT_TRIGGERS_PATH = "/admin/hub-and-spoke/event-triggers";

export const EventTriggerViewer = () => {
  const {eventTriggerId} = useParams<{eventTriggerId: string}>();
  const {data: eventTriggerData, isLoading: isLoadingEventTrigger} =
    useEventTriggersQuery({
      eventTriggerId,
      options: [{enabled: !!eventTriggerId}],
    });

  const eventTrigger = Array.isArray(eventTriggerData)
    ? eventTriggerData[0]
    : eventTriggerData;

  return (
    <HubAndSpokePage
      title="Event Trigger"
      actions={
        <>
          <ButtonLink to={EVENT_TRIGGERS_PATH}>Back to list</ButtonLink>
          <ButtonLink
            to={`${EVENT_TRIGGERS_PATH}/edit/${eventTriggerId}`}
            color="primary"
            outline={false}
          >
            Edit
          </ButtonLink>
        </>
      }
    >
      {isLoadingEventTrigger ? (
        <Spinner />
      ) : !eventTrigger ? (
        <FormSectionHint>Event trigger not found.</FormSectionHint>
      ) : (
        <FormCardWrapper>
          <FormSection>
            <FormSectionTitle>Details</FormSectionTitle>
            <Details>
              <dt>Template</dt>
              <dd>{eventTrigger.template_field.template_name}</dd>
              <dt>Workflow</dt>
              <dd>
                {getWorkflowLabel(eventTrigger.template_field.workflow_type)}
              </dd>
              <dt>Template field</dt>
              <dd>{eventTrigger.template_field.friendly_name}</dd>
              <dt>Document</dt>
              <dd>{eventTrigger.document}</dd>
              <dt>Facilities</dt>
              <dd>
                {eventTrigger.facilities
                  .map(facility => facility.name)
                  .join(", ")}
              </dd>
              <dt>SFTP</dt>
              <dd>
                {eventTrigger.sftp_config.name} (
                {eventTrigger.sftp_config.hostname})
              </dd>
              <dt>Target path</dt>
              <dd>
                <code>{eventTrigger.document_path}</code>
              </dd>
              <dt>Upload on</dt>
              <dd>{eventTrigger.upload_on.map(getUploadOnLabel).join(", ")}</dd>
            </Details>
          </FormSection>
          <FormSection>
            <FormSectionTitle>Rule preview</FormSectionTitle>
            <FormSectionHint>
              Select the answer to see what the trigger will do.
            </FormSectionHint>
            <EventTriggerViewerForm eventTrigger={eventTrigger} />
          </FormSection>
        </FormCardWrapper>
      )}
    </HubAndSpokePage>
  );
};

const Details = styled.dl`
  display: grid;
  grid-template-columns: 160px 1fr;
  row-gap: ${({theme}) => theme.space[2]}px;
  column-gap: ${({theme}) => theme.space[4]}px;
  margin: 0;

  dt {
    color: ${({theme}) => theme.color.gray150};
  }

  dd {
    margin: 0;
    word-break: break-word;
  }
`;
